import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  verifyBookingStatusApi,
  verifyTicketStatusApi,
  BookingVerificationPayload,
  TicketVerificationPayload
} from '../../services/qrScanService';

interface QRScanState {
  isLoading: boolean;
  scanResult: any | null;
  error: string | null;
  success: boolean;
  bookingDetail: any | null;
  ticketId: string | null; // Store ticketId from QR scan
  userId: string | null; // Store userId from QR scan
  firstName: string | null; // Store first name from QR scan or modal
  lastName: string | null; // Store last name from QR scan or modal
  specificErrorMessage: string | null; // Store specific error message from API
  errorStatusCode: number | null; // Store error status code
  paymentQR: string | null; // Store payment QR code URL
  paymentStatus: 'pending' | 'success' | 'failed' | null; // Payment status from socket
  tempDataId: string | null; // Store temp_data_id for payment flow
}

const initialState: QRScanState = {
  isLoading: false,
  scanResult: null,
  error: null,
  success: false,
  bookingDetail: null,
  ticketId: null,
  userId: null,
  firstName: null,
  lastName: null,
  specificErrorMessage: null,
  errorStatusCode: null,
  paymentQR: null,
  paymentStatus: null,
  tempDataId: null,
};

// Async thunk for booking verification
export const verifyBookingStatus = createAsyncThunk(
  'qrScan/verifyBookingStatus',
  async (payload: BookingVerificationPayload, { rejectWithValue }) => {
    try {
      const response = await verifyBookingStatusApi(payload);
      if (response.success) {
        return response;
      } else {
        return rejectWithValue({
          error: response.error || 'Verification failed',
          specificErrorMessage: response.error,
          statusCode: response.statusCode
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        error: error.message || 'Network error',
        specificErrorMessage: error.message || 'Network error',
        statusCode: null
      });
    }
  }
);

// Async thunk for ticket verification
export const verifyTicketStatus = createAsyncThunk(
  'qrScan/verifyTicketStatus',
  async (payload: TicketVerificationPayload, { rejectWithValue }) => {
    try {
      const response = await verifyTicketStatusApi(payload);
      if (response.success) {
        return response;
      } else {
        return rejectWithValue({
          error: response.error || 'Verification failed',
          specificErrorMessage: response.error,
          statusCode: response.statusCode
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        error: error.message || 'Network error',
        specificErrorMessage: error.message || 'Network error',
        statusCode: null
      });
    }
  }
);

const qrScanSlice = createSlice({
  name: 'qrScan',
  initialState,
  reducers: {
    setLoadingState: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setBookingDetail: (state, action: PayloadAction<any>) => {
      state.bookingDetail = action.payload;
    },
    clearScanResult: (state) => {
      state.scanResult = null;
      state.error = null;
      state.success = false;
      state.ticketId = null;
      state.userId = null;
      state.firstName = null;
      state.lastName = null;
      state.specificErrorMessage = null;
      state.errorStatusCode = null;
      state.tempDataId = null;
    },
    setUserNames: (state, action: PayloadAction<{ firstName: string, lastName: string }>) => {
      state.firstName = action.payload.firstName;
      state.lastName = action.payload.lastName;
    },
    setPaymentQR: (state, action: PayloadAction<string | null>) => {
      state.paymentQR = action.payload;
    },
    setPaymentStatus: (state, action: PayloadAction<'pending' | 'success' | 'failed' | null>) => {
      state.paymentStatus = action.payload;
    },
    setTempDataId: (state, action: PayloadAction<string | null>) => {
      state.tempDataId = action.payload;
    },
    resetQRScanState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Booking verification
      .addCase(verifyBookingStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(verifyBookingStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.scanResult = action.payload;
        state.bookingDetail = action.payload.data;
        // Extract ticketId, userId, and names from the API response
        state.ticketId = action.payload.data?.id || null;
        state.userId = action.payload.data?.user_id || null;
        state.firstName = action.payload.data?.first_name || null;
        state.lastName = action.payload.data?.last_name || null;
        state.error = null;
      })
      .addCase(verifyBookingStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        const payload = action.payload as any;
        state.error = payload?.error || 'Verification failed';
        state.specificErrorMessage = payload?.specificErrorMessage || null;
        state.errorStatusCode = payload?.statusCode || null;
        state.scanResult = null;
      })
      // Ticket verification
      .addCase(verifyTicketStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(verifyTicketStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.scanResult = action.payload;
        state.bookingDetail = action.payload.data;
        // Extract ticketId, userId, and names from the API response
        state.ticketId = action.payload.data?.id || null;
        state.userId = action.payload.data?.user_id || null;
        state.firstName = action.payload.data?.first_name || null;
        state.lastName = action.payload.data?.last_name || null;
        state.error = null;
      })
      .addCase(verifyTicketStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        const payload = action.payload as any;
        state.error = payload?.error || 'Verification failed';
        state.specificErrorMessage = payload?.specificErrorMessage || null;
        state.errorStatusCode = payload?.statusCode || null;
        state.scanResult = null;
      });
  },
});

export const {
  setLoadingState,
  setBookingDetail,
  clearScanResult,
  resetQRScanState,
  setUserNames,
  setPaymentQR,
  setPaymentStatus,
  setTempDataId
} = qrScanSlice.actions;

export default qrScanSlice.reducer;
